import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalSize } from '@/shared/components/ui/Modal';
import ViewSettingsForm from './ViewSettingsForm';
import { ViewSettings } from './types';
import { usePreferences } from '@/shared/hooks/usePreferences';
import { LoadingState } from '@/shared/ui/states/LoadingState';
import { ErrorState } from '@/shared/ui/states/ErrorState';

interface ViewSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ViewSettingsModal: React.FC<ViewSettingsModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { data: preferences, isLoading, isFetching, isError, error, refetch, update, isUpdating } = usePreferences({ autoFetch: false });
  const hasFetchedOnOpen = useRef(false);

  // Incremented each time a fetch completes with fresh data so ViewSettingsForm
  // remounts with updated initialValues. ViewSettingsForm uses useState(initialValues)
  // which only reads the prop on mount — without a key change it would show stale values
  // from its internal state even though the cache (preferences) already has fresh data.
  const [formKey, setFormKey] = useState(0);
  const wasFetchingRef = useRef(false);
  useEffect(() => {
    if (wasFetchingRef.current && !isFetching && preferences) {
      // Fetch just completed with data — bump the key to remount the form.
      setFormKey((k) => k + 1);
    }
    wasFetchingRef.current = isFetching;
  }, [isFetching, preferences]);

  useEffect(() => {
    if (isOpen && !hasFetchedOnOpen.current) {
      refetch();
      hasFetchedOnOpen.current = true;
    } else if (!isOpen) {
      hasFetchedOnOpen.current = false;
    }
  }, [isOpen, refetch]);

  const handleSubmit = (values: ViewSettings) => {
    update(values, {
      onSuccess: () => {
        // Close the modal immediately.
        // UserPreferencesProvider subscribes to the React Query cache and syncs
        // its state instantly when invalidateQueries causes the ['preferences']
        // cache to update — no explicit refetchPreferences() call needed.
        onClose();
      }
    });
  }

  // True whenever any async operation is in flight after the user clicked Apply:
  //   - isUpdating: the PATCH/PUT to save preferences
  //   - isFetching: the background refetch after invalidation (only when we already
  //     have data — on the initial open isLoading covers the no-data case)
  const isBusy = isUpdating || isFetching;

  const renderContent = () => {
    // Initial fetch — no data yet, show a standalone spinner.
    if (isLoading) {
      return <LoadingState />;
    }

    if (isError) {
      return <ErrorState error={error ? error.message : 'An unknown error occurred'} />;
    }

    if (preferences) {
      // Keep the form mounted while busy (saving or background refetch) so the
      // modal height stays stable. Show a single overlay spinner instead of
      // toggling between form and spinner which causes height jumps.
      return (
        <div className="relative">
          {isBusy && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-surface/60 rounded">
              <LoadingState />
            </div>
          )}
          <ViewSettingsForm
            key={formKey}
            initialValues={preferences}
            onSubmit={handleSubmit}
            onCancel={onClose}
            isUpdating={isBusy}
          />
        </div>
      );
    }

    return null;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('viewSettings.title')}
      size={ModalSize.Xxxl}
      preventCloseOnEsc
    >
      {renderContent()}
    </Modal>
  );
};

export default ViewSettingsModal;
